/* eslint-disable react/prop-types */
import React from 'react';
import Pagination from "react-js-pagination";
import TableSkeleton from "../active-users/states/TableSkeleton";

/**
 * The individual breath sessions behind the breath_sessions count (042).
 *
 * The header states the total explicitly and next to the expected count, because the
 * whole point of this table is that the two agree: if the box says 122, this says
 * 122. A silent mismatch between an aggregate and its own detail rows is the kind of
 * thing that erodes trust in every other number on the page.
 */

// breath_time / breath_perform_time are TEXT columns holding millisecond strings
// (e.g. "60000"), sometimes empty or non-numeric — guard before dividing.
const secs = (v) => {
  if (v === null || v === undefined || v === '') return '-';
  const n = Number(v);
  if (!Number.isFinite(n)) return '-';
  return `${(n / 1000).toFixed(1)}s`;
};

const deviceLabel = (v) => {
  if (v === 'true') return 'Watch';
  if (v === 'false') return 'Mobile';
  return '-';
};

const PauseAlertImpactBreathTable = ({
  rows = [],
  totalItem = 0,
  expectedTotal = null,
  page = 1,
  limit = 20,
  loading = false,
  onPageChange,
}) => {
  const mismatch = expectedTotal !== null && Number(expectedTotal) !== Number(totalItem);

  return (
    <div className="au-panel" data-testid="pai-breath-panel">
      <div className="au-panel__head">
        <h4 className="au-panel__title">
          Breath sessions <span className="pai-count" data-testid="pai-breath-total">({totalItem})</span>
        </h4>
        <span className="au-spacer" />
      </div>

      {mismatch && (
        <p className="au-chart-note pai-truncated" data-testid="pai-breath-mismatch">
          This list holds {totalItem} records but the summary reports {expectedTotal}. The two are
          built from the same filters, so a difference means something changed between the two
          requests — reload before trusting either number.
        </p>
      )}

      {loading ? (
        <TableSkeleton rows={8} />
      ) : rows.length === 0 ? (
        <p className="au-chart-empty" data-testid="pai-breath-empty">
          No breath sessions for this user in the selected range.
        </p>
      ) : (
        <>
          <div className="au-table-wrap">
            <table className="au-table pai-table" data-testid="pai-breath-table">
              <thead>
                <tr>
                  <th>No</th>
                  <th>Recorded (UTC)</th>
                  <th>Perform time</th>
                  <th>Breath time</th>
                  <th>Type</th>
                  <th>Pattern</th>
                  <th>Cycle</th>
                  <th>Cycle length</th>
                  <th>Inhale</th>
                  <th>Exhale</th>
                  <th>Hold</th>
                  <th>Device</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, index) => (
                  <tr key={r.id ?? index}>
                    <td data-label="No" className="tnum">{(page - 1) * limit + index + 1}</td>
                    <td data-label="Recorded (UTC)">{r.timestamp_utc || '-'}</td>
                    <td data-label="Perform time" className="tnum">{secs(r.breath_perform_time)}</td>
                    <td data-label="Breath time" className="tnum">{secs(r.breath_time)}</td>
                    <td data-label="Type">{r.breath_type || '-'}</td>
                    <td data-label="Pattern">{r.breath_pattern || '-'}</td>
                    <td data-label="Cycle" className="tnum">{r.breath_cycle || '-'}</td>
                    <td data-label="Cycle length" className="tnum">{r.breath_cycle_length || '-'}</td>
                    <td data-label="Inhale" className="tnum">{secs(r.breath_inhale)}</td>
                    <td data-label="Exhale" className="tnum">{secs(r.breath_exhale)}</td>
                    <td data-label="Hold" className="tnum">{secs(r.breath_hold)}</td>
                    <td data-label="Device">{deviceLabel(r.is_from_watch)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalItem > limit && (
            <Pagination
              activePage={page}
              itemsCountPerPage={limit}
              totalItemsCount={totalItem}
              pageRangeDisplayed={5}
              onChange={onPageChange}
            />
          )}
        </>
      )}
    </div>
  );
};

export default PauseAlertImpactBreathTable;
